import React, { useMemo } from "react";
import { QCFinding, QCSectionResult } from "../../types/qcTypes";
import IndependentQCResultsView from "./IndependentQCResultsView";

// Independent QC's document-mode view. Renders the entire uploaded document
// as one continuous markdown stream (headings, lists, prose flow naturally)
// with findings still highlighted inline and resolvable via the same icons
// used in the per-section flows.
//
// Implementation note: this is a thin wrapper around IndependentQCResultsView
// (the markdown-rendering findings view used only by independent QC). It merges
// every section's `sectionContent` into one markdown string, translates each
// finding's StartOffset/EndOffset into the merged content's offset space, and
// then hands that to IndependentQCResultsView with `preserveHeadings={true}` so
// the heading downgrade applied in panel-mode is skipped. All other behavior
// (popovers, edit flow, edited/deleted chip, outside-click close, accept/
// decline/edit/revert handlers, top toolbar with counts and bulk actions) is
// reused unchanged from IndependentQCResultsView.

interface IndependentQCDocumentViewProps {
  sections: QCSectionResult[];
  onAcceptFinding: (
    findingId: number,
    editedText?: string | null,
  ) => void | Promise<void>;
  onDeclineFinding: (findingId: number) => void | Promise<void>;
  onRevertFinding?: (findingId: number) => void | Promise<void>;
  // Document-level bulk handlers -- operate across every finding in the doc.
  onAcceptAll: () => void | Promise<void>;
  onDeclineAll: () => void | Promise<void>;
  onRevertAll?: () => void | Promise<void>;
  onSubmitFeedback?: (
    findingId: number,
    helpful: boolean,
  ) => void | Promise<void>;
  readOnly?: boolean;
}

// Merge every section's content into one markdown string with each section's
// title injected as a `# Heading` so the document's hierarchy survives. For
// every finding, translate its offsets from section-local space to merged
// space so the existing rehype plugin finds the right text.
function mergeSections(sections: QCSectionResult[]): {
  content: string;
  findings: QCFinding[];
} {
  let merged = "";
  const mergedFindings: QCFinding[] = [];
  for (const s of sections) {
    if (merged) merged += "\n\n";
    if (s.sectionTitle) {
      merged += `# ${s.sectionTitle}\n\n`;
    }
    const contentStart = merged.length;
    merged += s.sectionContent || "";
    for (const f of s.findings) {
      mergedFindings.push({
        ...f,
        StartOffset: contentStart + f.StartOffset,
        EndOffset: contentStart + f.EndOffset,
      });
    }
  }
  return { content: merged, findings: mergedFindings };
}

const IndependentQCDocumentView: React.FC<IndependentQCDocumentViewProps> = ({
  sections,
  onAcceptFinding,
  onDeclineFinding,
  onRevertFinding,
  onAcceptAll,
  onDeclineAll,
  onRevertAll,
  onSubmitFeedback,
  readOnly = false,
}) => {
  const { content, findings } = useMemo(() => mergeSections(sections), [sections]);

  return (
    <IndependentQCResultsView
      findings={findings}
      sectionContent={content}
      sectionTitle=""
      onAcceptFinding={onAcceptFinding}
      onDeclineFinding={onDeclineFinding}
      onRevertFinding={onRevertFinding}
      onAcceptAll={onAcceptAll}
      onDeclineAll={onDeclineAll}
      onRevertAll={onRevertAll}
      onSubmitFeedback={onSubmitFeedback}
      readOnly={readOnly}
      preserveHeadings
    />
  );
};

export default IndependentQCDocumentView;
